/**
 * 📡 Merkezi Realtime Servisi — Tip Tanımları
 * DrawRealtimeManager, connectionMonitor ve useDrawRealtime tarafından kullanılır.
 */

// ─── Connection State Machine ───

export type ConnectionState =
  | 'idle'          // Henüz bağlanmamış
  | 'connecting'    // WebSocket kuruluyor
  | 'connected'     // Tüm kanallar aktif
  | 'reconnecting'  // Bağlantı koptu, yeniden bağlanıyor
  | 'failed'        // Retry limiti aşıldı
  | 'degraded'      // Realtime çalışmıyor, polling aktif

// ─── Draw Broadcast Events ───

export interface DrawStartingEvent {
  type: 'draw_starting'
  payload: { groupId: string }
}

export interface TurnRevealedEvent {
  type: 'turn_revealed'
  payload: {
    groupId: string
    turnIndex: number
    winnerId: string
    winnerName: string
    paymentDate: string
    spinDuration: number
    intermediateNames?: string[]
  }
}

export interface DrawCompleteEvent {
  type: 'draw_complete'
  payload: {
    groupId: string
    totalTurns: number
  }
}

export type DrawEvent = DrawStartingEvent | TurnRevealedEvent | DrawCompleteEvent

// ─── Group Sync ───

export interface GroupSyncPayload {
  id: string
  draw_scheduled_at?: string | null
  status?: string
  is_locked?: boolean
  draw_notified_stage?: number
  draw_processing?: boolean
  [key: string]: unknown
}

// ─── Presence ───

export interface SpectatorInfo {
  userId: string
  displayName: string
  avatarUrl: string | null
  joinedAt: string
}

export interface PresenceState {
  spectatorCount: number
  spectators: SpectatorInfo[]
}

// ─── Manager Callbacks ───

export interface DrawRealtimeCallbacks {
  onGroupUpdate: (group: GroupSyncPayload) => void
  onDrawStarting: () => void
  onTurnRevealed: (payload: TurnRevealedEvent['payload']) => void
  onDrawComplete: (payload: DrawCompleteEvent['payload']) => void
  onPresenceSync: (state: PresenceState) => void
  onConnectionStateChange: (state: ConnectionState) => void
}

// ─── Manager State ───

export interface DrawRealtimeState {
  connectionState: ConnectionState
  groupId: string | null
  ceremonyActive: boolean
  presence: PresenceState
  eventQueue: DrawEvent[]
  isProcessingEvent: boolean
}

// ─── Reconnection Config ───

export const RECONNECT_DELAYS = [2000, 4000, 8000, 16000, 30000] as const
export const MAX_RECONNECT_ATTEMPTS = RECONNECT_DELAYS.length + 1
export const HEARTBEAT_INTERVAL = 30_000
export const POLLING_INTERVAL = 5_000
export const CONNECTING_TIMEOUT = 10_000
export const BACKGROUND_RECONNECT_INTERVAL = 60_000

// ─── Notification Sync Config ───

export const CELEBRATION_DELAY = 4_000   // Kutlama overlay süresi (ms)
export const TRANSITION_DELAY = 2_000    // Turlar arası geçiş (ms)
export const NOTIFICATION_BUFFER = 5_000 // Güvenlik tamponu (ms)

/** Animasyon toplam süre hesaplama */
export function calculateNotificationDelay(
  turnCount: number,
  spinDuration: number
): number {
  return turnCount * (spinDuration + CELEBRATION_DELAY + TRANSITION_DELAY) + NOTIFICATION_BUFFER
}

// ─── Toast Suppression (Spoiler Önleme) ───

export const SUPPRESSED_NOTIFICATION_SLUGS = new Set([
  'draw_result',
  'lottery_result',
  'manual_draw_result',
])
